import React, { createContext, useContext, useState, useEffect } from 'react';
import { experiencesService } from '../services/experiencesService';

type ExchangeRates = Record<string, number>;

type CurrencyContextType = {
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
  exchangeRates: ExchangeRates;
  isLoading: boolean;
  error: string | null;
  convert: (value: number, fromCurrency: string) => { value: number, code: string };
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const currencies = {
  USD: { code: 'USD', symbol: '$' },
  EUR: { code: 'EUR', symbol: '€' },
  GBP: { code: 'GBP', symbol: '£' },
  JPY: { code: 'JPY', symbol: '¥' },
  AUD: { code: 'AUD', symbol: 'A$' },
  CAD: { code: 'CAD', symbol: 'C$' },
  CHF: { code: 'CHF', symbol: 'Fr' },
  CNY: { code: 'CNY', symbol: '¥' },
  HKD: { code: 'HKD', symbol: 'HK$' },
  NZD: { code: 'NZD', symbol: 'NZ$' },
  AED: { code: 'AED', symbol: 'د.إ' },
  ARS: { code: 'ARS', symbol: '$' },
  BRL: { code: 'BRL', symbol: 'R$' },
  CLP: { code: 'CLP', symbol: '$' },
  COP: { code: 'COP', symbol: '$' },
  DKK: { code: 'DKK', symbol: 'kr' },
  FJD: { code: 'FJD', symbol: 'FJ$' },
  IDR: { code: 'IDR', symbol: 'Rp' },
  ILS: { code: 'ILS', symbol: '₪' },
  INR: { code: 'INR', symbol: '₹' },
  ISK: { code: 'ISK', symbol: 'kr' },
  KRW: { code: 'KRW', symbol: '₩' },
  MXN: { code: 'MXN', symbol: '$' },
  MYR: { code: 'MYR', symbol: 'RM' },
  NOK: { code: 'NOK', symbol: 'kr' },
  PEN: { code: 'PEN', symbol: 'S/' },
  PHP: { code: 'PHP', symbol: '₱' },
  PLN: { code: 'PLN', symbol: 'zł' },
  RUB: { code: 'RUB', symbol: '₽' },
  SEK: { code: 'SEK', symbol: 'kr' },
  SGD: { code: 'SGD', symbol: 'S$' },
  THB: { code: 'THB', symbol: '฿' },
  TRY: { code: 'TRY', symbol: '₺' },
  TWD: { code: 'TWD', symbol: 'NT$' },
  VND: { code: 'VND', symbol: '₫' },
  ZAR: { code: 'ZAR', symbol: 'R' }
};

export const CURRENCY_OPTIONS = Object.values(currencies).map(currency => ({
  code: currency.code,
  name: getCurrencyName(currency.code),
  symbol: currency.symbol
}));

// This is kept separated since we will need to support i18n soon.
function getCurrencyName(code: string): string {
  const names: Record<string, string> = {
    USD: 'US Dollar',
    EUR: 'Euro',
    GBP: 'British Pound',
    JPY: 'Japanese Yen',
    AUD: 'Australian Dollar',
    CAD: 'Canadian Dollar',
    CHF: 'Swiss Franc',
    CNY: 'Chinese Yuan',
    HKD: 'Hong Kong Dollar',
    NZD: 'New Zealand Dollar',
    AED: 'UAE Dirham',
    ARS: 'Argentine Peso',
    BRL: 'Brazilian Real',
    CLP: 'Chilean Peso',
    COP: 'Colombian Peso',
    DKK: 'Danish Krone',
    FJD: 'Fijian Dollar',
    IDR: 'Indonesian Rupiah',
    ILS: 'Israeli Shekel',
    INR: 'Indian Rupee',
    ISK: 'Icelandic Króna',
    KRW: 'South Korean Won',
    MXN: 'Mexican Peso',
    MYR: 'Malaysian Ringgit',
    NOK: 'Norwegian Krone',
    PEN: 'Peruvian Sol',
    PHP: 'Philippine Peso',
    PLN: 'Polish Złoty',
    RUB: 'Russian Ruble',
    SEK: 'Swedish Krona',
    SGD: 'Singapore Dollar',
    THB: 'Thai Baht',
    TRY: 'Turkish Lira',
    TWD: 'Taiwan Dollar',
    VND: 'Vietnamese Dong',
    ZAR: 'South African Rand'
  };
  return names[code] || code;
}

export const CurrencyProvider: React.FC<{ children: React.ReactNode, productCurrencyCode: string }> = ({ children, productCurrencyCode }) => {
  // The default currency we transform the Product pricing to.
  const [selectedCurrency, setSelectedCurrency] = useState('ARS');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExchangeRates = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await experiencesService.getExchangeRates([productCurrencyCode], [selectedCurrency]);

      // Transform the response into a simple rates object
      const rates: ExchangeRates = {};
      if (response && response.rates) {
        response.rates.forEach((rateObj: {
          sourceCurrency: string;
          targetCurrency: string;
          rate: number;
        }) => {
          const key = `${rateObj.sourceCurrency}${rateObj.targetCurrency}`;
          rates[key] = rateObj.rate;
        });
      }
      setExchangeRates(rates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch exchange rates');
    } finally {
      setIsLoading(false);
    }
  };

  const convert = (value: number, fromCurrency: string) => {
    const rateKey = `${fromCurrency}${selectedCurrency}`;
    const rate = exchangeRates[rateKey];

    if (!rate) {
      return {
        value,
        code: currencies[fromCurrency as keyof typeof currencies]?.code || fromCurrency
      };
    }

    return {
      value: Number((value * rate).toFixed(2)),
      code: currencies[selectedCurrency as keyof typeof currencies]?.code || selectedCurrency
    };
  };

  useEffect(() => {
    fetchExchangeRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CurrencyContext.Provider value={{
      selectedCurrency,
      setSelectedCurrency,
      exchangeRates,
      isLoading,
      error,
      convert
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}; 